import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import NavbarStandard from 'components/pages/landing/NavbarStandard';
import FooterStandard from 'components/pages/landing/FooterStandard';
import { fetchPublicBlogPosts } from 'helpers/blog';

const MAX_FEATURED_ITEMS = 3;

const getLineClampStyle = lines => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden'
});

const PublicBlogListPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchPublicBlogPosts();
        setPosts(response.posts || []);
      } catch (requestError) {
        setPosts([]);
        setError(requestError.response?.data?.error || t('BLOG.ERROR_LOAD'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [t]);

  const { featuredPosts, latestPosts } = useMemo(() => {
    const featured = posts.filter(post => post.is_featured).slice(0, MAX_FEATURED_ITEMS);
    const featuredIds = new Set(featured.map(post => post.id));
    return {
      featuredPosts: featured,
      latestPosts: posts.filter(post => !featuredIds.has(post.id))
    };
  }, [posts]);

  return (
    <>
      <NavbarStandard solid />
      <main>
        <section className="bg-300 py-6 mt-6">
          <Container>
            <h1 className="mb-2">{t('BLOG.PUBLIC_TITLE')}</h1>
            <p className="text-700 mb-0">{t('BLOG.PUBLIC_DESCRIPTION')}</p>
          </Container>
        </section>

        <section className="py-5">
          <Container>
            {loading ? (
              <div className="text-center py-6">
                <Spinner animation="border" />
              </div>
            ) : null}

            {!loading && error ? <Alert variant="danger">{error}</Alert> : null}

            {!loading && !error && !posts.length ? (
              <Alert variant="light">{t('BLOG.EMPTY')}</Alert>
            ) : null}

            {!loading && !error && featuredPosts.length ? (
              <>
                <h4 className="mb-3">{t('BLOG.FEATURED_SECTION')}</h4>
                <Row className="g-4 mb-5">
                  {featuredPosts.map(post => (
                    <Col lg={4} md={6} key={post.id}>
                      <Card className="h-100 border">
                        <div className="ratio ratio-4x3">
                          {post.cover_image_url ? (
                            <img
                              src={post.cover_image_url}
                              alt={post.title}
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${post.accent_color || '#0b5ed7'}, #0b1727)` }} />
                          )}
                        </div>
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex gap-2 flex-wrap mb-2">
                            <Badge bg="warning" text="dark">{t('BLOG.STATUS_FEATURED')}</Badge>
                            {post.category ? <Badge bg="light" text="dark">{post.category}</Badge> : null}
                          </div>
                          <h5 className="mb-2" style={getLineClampStyle(3)}>{post.title}</h5>
                          <p className="text-700 mb-3" style={getLineClampStyle(4)}>
                            {post.excerpt || t('BLOG.LANDING_FALLBACK')}
                          </p>
                          <Button as={Link} to={`/blog/${post.slug}`} variant="outline-primary" size="sm" className="mt-auto">
                            {t('BLOG.READ_POST')}
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            ) : null}

            {!loading && !error && latestPosts.length ? (
              <>
                <h4 className="mb-3">{t('BLOG.LATEST_SECTION')}</h4>
                <Row className="g-4">
                  {latestPosts.map(post => (
                    <Col lg={4} md={6} key={post.id}>
                      <Card className="h-100 border">
                        <div className="ratio ratio-4x3">
                          {post.cover_image_url ? (
                            <img
                              src={post.cover_image_url}
                              alt={post.title}
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${post.accent_color || '#0b5ed7'}, #0b1727)` }} />
                          )}
                        </div>
                        <Card.Body className="d-flex flex-column">
                          <div className="text-500 fs--2 mb-2">
                            {[post.category, post.author_name || post.author?.full_name || 'Gestodo', post.reading_time].filter(Boolean).join(' · ')}
                          </div>
                          <h5 className="mb-2" style={getLineClampStyle(3)}>{post.title}</h5>
                          {post.subtitle ? (
                            <p className="text-700 mb-2" style={getLineClampStyle(2)}>
                              {post.subtitle}
                            </p>
                          ) : null}
                          <p className="text-600 mb-3" style={getLineClampStyle(4)}>
                            {post.excerpt || t('BLOG.LANDING_FALLBACK')}
                          </p>
                          <Button as={Link} to={`/blog/${post.slug}`} variant="outline-primary" size="sm" className="mt-auto">
                            {t('BLOG.READ_POST')}
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            ) : null}
          </Container>
        </section>
      </main>
      <FooterStandard />
    </>
  );
};

export default PublicBlogListPage;
